// extraCardRender.js – Extrakarten (Spielideen, Legende …) als Druckbild
// Gleicher Kartenaufbau wie die Memo-Karten: runder Rahmen, weisse Fläche, Handschrift
// in Kapitälchen (siehe smallCaps.js).
import { EXTRA_PURPLE } from './extraCards';
import { scWidth, scFill } from './smallCaps';

// Masse wie bei den Memo-Karten (siehe cardRender.js).
const CARD_PX = Math.round((6 / 2.54) * 300); // 709 = 6 cm bei 300 dpi
const FONT = "'MemoHand', 'Bandeleart', 'Comic Sans MS', cursive";
// Helles Violett der Trennlinie unter dem Titel; INK = fast schwarze Textfarbe.
const LINE_PURPLE = '#b39bd0';
const INK = '#111';

function roundRect(ctx, x, y, w, h, r) {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.arcTo(x + w, y, x + w, y + h, r);
  ctx.arcTo(x + w, y + h, x, y + h, r);
  ctx.arcTo(x, y + h, x, y, r);
  ctx.arcTo(x, y, x + w, y, r);
  ctx.closePath();
}

// „Text mit [violetten] Stellen“ → Wörter mit Farbe
// Zerlegt einen Text in Wörter und merkt sich pro Wort die Farbe.
// Alles zwischen [ und ] wird violett, der Rest in baseColor.
function words(text, baseColor) {
  const out = [];
  let purple = false;
  text.split(/(\[|\])/).forEach(part => {
    if (part === '[') { purple = true; return; }
    if (part === ']') { purple = false; return; }
    part.split(/(\s+)/).forEach(w => {
      if (!w) return;
      if (/^\s+$/.test(w)) out.push({ space: true });
      else out.push({ text: w, color: purple ? EXTRA_PURPLE : baseColor });
    });
  });
  return out;
}

// Wörter in Zeilen umbrechen; jede Zeile = Liste von Segmenten {text, color}
// Zeilenumbruch: Wörter werden in eine Zeile gelegt, bis die Breite maxW erreicht ist,
// dann beginnt eine neue Zeile. Leerzeichen am Zeilenanfang fallen weg.
function wrap(ctx, ws, maxW, fs) {
  const lines = [];
  let line = [], width = 0, pendingSpace = false;
  const spaceW = scWidth(ctx, ' ', fs, FONT);
  for (const w of ws) {
    if (w.space) { pendingSpace = line.length > 0; continue; }
    const ww = scWidth(ctx, w.text, fs, FONT);
    const add = (pendingSpace ? spaceW : 0) + ww;
    if (line.length && width + add > maxW) {
      lines.push({ segs: line, width });
      line = [{ ...w }]; width = ww;
    } else {
      if (pendingSpace && line.length) line.push({ text: ' ', color: w.color });
      line.push({ ...w }); width += add;
    }
    pendingSpace = false;
  }
  if (line.length) lines.push({ segs: line, width });
  return lines;
}

// Zeichnet eine Zeile aus farbigen Stücken nebeneinander (in Kapitälchen).
function drawSegs(ctx, segs, x, baseline, fs) {
  let cx = x;
  for (const s of segs) {
    ctx.fillStyle = s.color;
    cx += scFill(ctx, s.text, cx, baseline, fs, FONT);
  }
}

// Inhalt eines Kartenkörpers in Zeilen auslegen (bei Schriftgrösse fs)
// Legt den Inhalt (body) einer Karte für eine bestimmte Schriftgrösse aus und
// liefert die Zeilen samt Gesamthöhe. Gezeichnet wird hier noch nichts – so kann
// man ausprobieren, ob der Text bei dieser Grösse auf die Karte passt.
function layoutBody(ctx, card, fs, width) {
  ctx.font = `${fs}px ${FONT}`;
  // pitch = Zeilenabstand, indent = Einzug für nummerierte Schritte.
  const pitch = fs * 1.36;
  const indent = scWidth(ctx, '0. ', fs, FONT) * 1.15;
  const rows = [];   // {segs, x, dy, prefix?}
  let y = 0;
  for (const item of card.body) {
    if (item.gap) { y += pitch * 0.7; continue; }
    if (item.zb) { rows.push({ segs: [{ text: 'z. B.:', color: EXTRA_PURPLE }], width: scWidth(ctx, 'z. B.:', fs, FONT), x: 0, y }); y += pitch; continue; }
    let prefix = null, text, color = INK, ind = 0;
    if (item.n !== undefined) { prefix = { text: `${item.n}.`, color: INK }; text = item.text; ind = indent; }
    else if (item.b !== undefined) { prefix = { text: '•', color: EXTRA_PURPLE }; text = item.b; color = EXTRA_PURPLE; ind = indent * 0.75; }
    else if (item.v !== undefined) { text = item.v; color = EXTRA_PURPLE; }
    else text = item.p;
    const lines = wrap(ctx, words(text, color), width - ind, fs);
    for (let i = 0; i < lines.length; i++) {
      rows.push({ segs: lines[i].segs, width: lines[i].width, x: ind, y, prefix: i === 0 ? prefix : null });
      y += pitch;
    }
  }
  return { rows, height: y - pitch + fs * 0.75 };
}

// Zeichnet eine Extrakarte als Bild (W × W Pixel). Ablauf:
// Rahmen → Titel (so gross wie möglich) → Untertitel → Trennlinie →
// Inhalt (Schrift wird verkleinert, bis alles Platz hat).
export async function renderExtraCardCanvas(card, W = CARD_PX) {
  // Alle Zeichen der Karte – damit der Browser genau die nötigen Schriftteile lädt.
  const sample = [card.titel, ...(card.untertitel || []), ...card.body.map(b => b.text || b.b || b.p || b.v || '')].join(' ');
  await document.fonts.load(`${Math.round(W * 0.1)}px MemoHand`, sample).catch(() => {});
  const canvas = document.createElement('canvas');
  canvas.width = W; canvas.height = W;
  const ctx = canvas.getContext('2d');

  // violetter Rahmen mit runden Ecken, weisse Fläche
  roundRect(ctx, 0, 0, W, W, 0.083 * W);
  ctx.fillStyle = EXTRA_PURPLE;
  ctx.fill();
  const inset = 0.094 * W;
  const I = W - 2 * inset;
  ctx.fillStyle = '#fff';
  ctx.fillRect(inset, inset, I, I);

  // padX = Abstand links/rechts, textW = verfügbare Textbreite, y = aktuelle Höhe beim Zeichnen.
  const padX = 0.045 * I;
  const textW = I - 2 * padX;
  const cx = inset + I / 2;
  let y = inset + 0.035 * I;

  // Titel (Zeilenumbruch über \n), so gross wie möglich
  const titleLines = card.titel.split('\n');
  // Schriftgrösse so wählen, dass die breiteste Titelzeile genau Platz hat (höchstens 13 % der Fläche).
  // Gemessen wird bei 100 px und dann hochgerechnet.
  const widest = Math.max(...titleLines.map(t => scWidth(ctx, t, 100, FONT)));
  const tfs = Math.min(0.13 * I, (textW * 100) / widest);
  ctx.font = `${tfs}px ${FONT}`;
  for (const t of titleLines) {
    y += tfs * 0.68;
    ctx.fillStyle = INK;
    scFill(ctx, t, cx, y, tfs, FONT, 'center');
    y += tfs * 0.14;
  }

  // Untertitel (violett)
  const subs = card.untertitel || [];
  if (subs.length) {
    const sw = Math.max(...subs.map(t => scWidth(ctx, t, 100, FONT)));
    const sfs = Math.min(0.056 * I, (textW * 100) / sw);
    ctx.font = `${sfs}px ${FONT}`;
    y += sfs * (card.center ? 1.0 : 0.35);
    for (const t of subs) {
      y += sfs * 0.75;
      ctx.fillStyle = EXTRA_PURPLE;
      scFill(ctx, t, cx, y, sfs, FONT, 'center');
      y += sfs * 0.38;
    }
  }

  // Trennlinie
  y += 0.025 * I;
  ctx.fillStyle = LINE_PURPLE;
  ctx.fillRect(inset + 0.02 * I, y, I - 0.04 * I, 0.009 * I);
  y += 0.009 * I;

  // Körper: Schrift so verkleinern, bis alles Platz hat; vertikal zentriert
  const top = y + 0.035 * I;
  const bottom = inset + I - 0.035 * I;
  const avail = bottom - top;
  let fs = 0.07 * I, lay;
  // Bei 7 % der Fläche beginnen und jeweils um 4 % verkleinern, bis der Inhalt passt.
  for (let k = 0; k < 40; k++) {
    lay = layoutBody(ctx, card, fs, textW);
    if (lay.height <= avail) break;
    fs *= 0.96;
  }
  // Senkrecht zentrieren, wenn Platz übrig ist.
  const start = top + Math.max(0, (avail - lay.height) / 2) + fs * 0.66;
  const left = inset + padX;
  for (const r of lay.rows) {
    const bx = card.center ? cx - r.width / 2 : left + r.x;
    // Aufzählungspunkt als kleiner gefüllter Kreis (der Punkt der Schrift wäre zu klein).
    if (r.prefix && r.prefix.text === '•') {
      ctx.beginPath();
      ctx.arc(left + fs * 0.18, start + r.y - fs * 0.24, fs * 0.11, 0, Math.PI * 2);
      ctx.fillStyle = r.prefix.color;
      ctx.fill();
    } else if (r.prefix) drawSegs(ctx, [r.prefix], left, start + r.y, fs);
    drawSegs(ctx, r.segs, bx, start + r.y, fs);
  }
  return canvas;
}
